/**
 * Wood (docs/upgrades.md, docs/wood.md): the second currency, the one dynasty upgrades are built
 * from. The workshop sets a peon's wood to an exact amount, and a peon buys it: with gold on the
 * Rewards screen, or with real money by e-transfer request. The limits and prices here are shared
 * by the server (server/db.ts), the Rewards screen, the workshop (src/components/DevSheet.tsx) and
 * the Node tests; never repeat the numbers anywhere else. Pure and dependency-free like
 * shared/upgrades.ts.
 */

export const WOOD_MIN = 0;
export const WOOD_MAX = 10_000_000;

/** Why `amount` cannot be set as a peon's wood, or null when it can. */
export function woodInputError(amount: unknown): string | null {
  if (typeof amount !== "number" || !Number.isInteger(amount)) return "Wood must be a whole number";
  if (amount < WOOD_MIN || amount > WOOD_MAX) return `Wood must be ${WOOD_MIN} to ${WOOD_MAX.toLocaleString("en-US")}`;
  return null;
}

// ---------- Buying wood (docs/wood.md) ----------

/** Wood in one pack: what one trade or one e-transfer request buys. */
export const WOOD_PACK = 100;
/** Gold one pack costs on the Rewards screen. */
export const WOOD_PACK_GOLD = 1000;
/** Real money one pack costs by e-transfer, in Canadian cents. */
export const WOOD_PACK_CAD_CENTS = 1000;
/** Real money one wood costs by e-transfer, in Canadian cents (the pack price spread over the pack). */
export const WOOD_CENTS_EACH = WOOD_PACK_CAD_CENTS / WOOD_PACK;
/** The least wood one e-transfer request buys (one dollar's worth). */
export const WOOD_ORDER_MIN = 10;
/** The most wood one e-transfer request buys (a thousand dollars' worth). */
export const WOOD_ORDER_MAX = 10_000;

/** What `wood` costs by e-transfer, in Canadian cents. */
export function woodOrderPrice(wood: number): number {
  return wood * WOOD_CENTS_EACH;
}

/** "10 to 10,000 wood": how much one e-transfer request may buy. */
export function formatWoodOrderBounds(): string {
  return `${WOOD_ORDER_MIN.toLocaleString("en-US")} to ${formatWoodPack(WOOD_ORDER_MAX)}`;
}

/** Why `wood` cannot be bought in one e-transfer request, or null when it can. */
export function woodOrderError(wood: unknown): string | null {
  if (typeof wood !== "number" || !Number.isInteger(wood)) return "Wood must be a whole number";
  if (wood < WOOD_ORDER_MIN || wood > WOOD_ORDER_MAX) return `Buy ${formatWoodOrderBounds()}`;
  return null;
}

/** How far back a peon's own requests are listed (GET /api/wood/orders?name=). */
export const WOOD_ORDER_RECENT_DAYS = 30;
/** The longest e-transfer email the workshop takes. */
export const ETRANSFER_EMAIL_MAX = 120;

/** "100 wood". */
export function formatWoodPack(wood: number = WOOD_PACK): string {
  return `${wood.toLocaleString("en-US")} wood`;
}

/** "1,000 gold". */
export function formatPackGold(gold: number = WOOD_PACK_GOLD): string {
  return `${gold.toLocaleString("en-US")} gold`;
}

/** "$10.00 CAD", or "$10 CAD" with `short` when the amount is whole dollars. */
export function formatCad(cents: number, short = false): string {
  const dollars = cents / 100;
  const text = short && Number.isInteger(dollars) ? dollars.toLocaleString("en-US") : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${text} CAD`;
}

/**
 * The reference code's characters: digits and capitals without 0, O, 1 or I, so it reads back the
 * same off a bank statement, a phone screen or a handwritten note.
 */
export const WOOD_REFERENCE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const WOOD_REFERENCE_PREFIX = "WOOD-";
export const WOOD_REFERENCE_LENGTH = 4;

/**
 * A fresh reference code for an e-transfer request, "WOOD-" plus four characters from
 * WOOD_REFERENCE_ALPHABET. `random` returns a number in [0, 1), like Math.random; the server passes
 * one backed by crypto.getRandomValues and retries on the rare collision (a unique index per
 * household).
 */
export function newWoodReference(random: () => number = Math.random): string {
  let code = "";
  for (let i = 0; i < WOOD_REFERENCE_LENGTH; i++) {
    const n = Math.min(WOOD_REFERENCE_ALPHABET.length - 1, Math.floor(random() * WOOD_REFERENCE_ALPHABET.length));
    code += WOOD_REFERENCE_ALPHABET[n];
  }
  return WOOD_REFERENCE_PREFIX + code;
}

/**
 * Why `email` cannot be the household's e-transfer destination, or null when it can. A loose shape
 * check only (something@something.something, no spaces): the bank is the real judge. The caller
 * trims first; an empty string is allowed, since it clears the setting.
 */
export function etransferEmailError(email: unknown): string | null {
  if (typeof email !== "string") return "The email must be text";
  if (email === "") return null;
  if (email.length > ETRANSFER_EMAIL_MAX) return `Keep the email to ${ETRANSFER_EMAIL_MAX} characters`;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "That does not look like an email";
  return null;
}

// ---------- Automatic confirmation by email (docs/wood.md, live-worker/src/mail.ts) ----------

/**
 * Domains whose DKIM signature (and From address) may credit an e-transfer request. A domain
 * matches itself and any subdomain ("interac.ca" takes "notify.payments.interac.ca"). If the first
 * real deposit email turns out to be signed by a bank instead, add its domain here.
 */
export const TRUSTED_SENDER_DOMAINS: readonly string[] = ["interac.ca"];

/** Whether `domain` is one of TRUSTED_SENDER_DOMAINS or a subdomain of one. */
export function isTrustedSenderDomain(domain: string | null | undefined): boolean {
  const d = (domain ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (!d) return false;
  return TRUSTED_SENDER_DOMAINS.some((t) => d === t || d.endsWith("." + t));
}

/** Who a request marked received by the deposit email names as having settled it. */
export const WOOD_EMAIL_SETTLER = "Interac email";

/** WOOD_REFERENCE_ALPHABET as a regex character class body (no 0, O, 1 or I). */
const REFERENCE_CHARS = "2-9A-HJ-NP-Z";

/**
 * Every reference code in `text`, uppercased, in order, without repeats. Case does not matter and
 * the dash may be a space or missing ("wood 7kqp", "WOOD7KQP"), since people type the e-transfer
 * message by hand; the four characters must still come from WOOD_REFERENCE_ALPHABET.
 */
export function findWoodReferences(text: string): string[] {
  const re = new RegExp(`(?<![A-Z0-9])WOOD[-\\s]?([${REFERENCE_CHARS}]{${WOOD_REFERENCE_LENGTH}})(?![A-Z0-9])`, "gi");
  const out: string[] = [];
  for (const m of text.matchAll(re)) {
    const code = WOOD_REFERENCE_PREFIX + m[1].toUpperCase();
    if (!out.includes(code)) out.push(code);
  }
  return out;
}

/**
 * The first dollar amount in `text`, in cents: "$10.00", "$ 1,234.50", "10.00 CAD" or
 * "10.00 (CAD)". Null when there is none.
 */
export function findCadAmountCents(text: string): number | null {
  const m = /\$\s?(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})(?!\d)|(?<![\d.,])(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})\s?(?:CAD|\(CAD\))/i.exec(text);
  if (!m) return null;
  const whole = (m[1] ?? m[3]).replace(/,/g, "");
  const cents = m[2] ?? m[4];
  return Number(whole) * 100 + Number(cents);
}
